/**
 * BrandLogo — VarunAIq wordmark + shield/ship mark.
 *
 * Built as a single SVG so it scales sharply at every size and sits cleanly
 * on dark or light surfaces (it picks its colors from the `tone` prop).
 *
 * The mark is a maritime shield (ship inside, two waves below) — meant to
 * read as "regulator-grade certainty" at a glance. The wordmark is rendered
 * in three weighted parts so the "AI" reads as the strong centre of the
 * brand: "Varun" (regular, slate/white) + "AI" (extra-bold, cyan) + "q"
 * (regular, slate/white, intentionally lowercase). The lowercase q balances
 * the silhouette of the wordmark and prevents the logo from reading as an
 * acronym ("VARUNAIQ") rather than a proper noun ("VarunAIq").
 *
 * Usage:
 *   <BrandLogo />                       // full lockup, dark surface
 *   <BrandLogo tone="light" />          // for light backgrounds
 *   <BrandLogo size="sm" tagline />     // smaller + show varunaiq.com tag
 *   <BrandLogo variant="compact" />     // hides the tagline & stacks tighter
 */
import React from 'react';

type Tone = 'dark' | 'light';
type Size = 'sm' | 'md' | 'lg' | 'xl';
type Variant = 'full' | 'compact' | 'mark';

interface Props {
  tone?: Tone;
  size?: Size;
  variant?: Variant;
  /** Show the small "varunaiq.com" tag under the wordmark. */
  tagline?: boolean;
  className?: string;
  ariaLabel?: string;
}

const SIZE_PX: Record<Size, { mark: number; word: number; tag: number }> = {
  sm: { mark: 28, word: 22, tag: 9  },
  md: { mark: 40, word: 30, tag: 11 },
  lg: { mark: 56, word: 42, tag: 13 },
  xl: { mark: 80, word: 60, tag: 15 },
};

export default function BrandLogo({
  tone = 'dark',
  size = 'md',
  variant = 'full',
  tagline = false,
  className = '',
  ariaLabel = 'VarunAIq',
}: Props) {
  const px = SIZE_PX[size];
  // The wordmark is intentionally split into three weights so "AI" carries
  // visual mass without overpowering the silhouette. Slate/white wraps
  // the cyan core.
  const wordColor   = tone === 'dark' ? '#F8FAFC' : '#0F172A';   // slate-50 / slate-900
  const accentColor = '#06B6D4';                                  // cyan-500 — the "AI"
  const subColor    = tone === 'dark' ? 'rgba(6,182,212,0.85)' : '#06B6D4';

  if (variant === 'mark') {
    return (
      <span
        role="img"
        aria-label={ariaLabel}
        className={`inline-flex items-center ${className}`}
        style={{ height: px.mark, width: px.mark }}
      >
        <Mark size={px.mark} tone={tone} />
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-2.5 ${className}`}
    >
      <Mark size={px.mark} tone={tone} />
      <span className="inline-flex flex-col leading-none">
        <span
          className="tracking-tight"
          style={{ fontSize: px.word, color: wordColor, letterSpacing: '-0.012em' }}
        >
          {/* "Varun" — medium weight, slate/white */}
          <span style={{ fontWeight: 500 }}>Varun</span>
          {/* "AI" — extra-bold cyan, slightly larger optical mass */}
          <span style={{ color: accentColor, fontWeight: 900 }}>AI</span>
          {/* "q" — lowercase, medium weight */}
          <span style={{ fontWeight: 500 }}>q</span>
        </span>
        {tagline && variant !== 'compact' && (
          <span
            className="mt-1 font-medium tracking-wider uppercase"
            style={{ fontSize: px.tag, color: subColor, letterSpacing: '0.08em' }}
          >
            varunaiq.com
          </span>
        )}
      </span>
    </span>
  );
}

/* ─── Mark ──────────────────────────────────────────────────────────────────
 * Shield silhouette + stylised ship (bow + bridge + funnel) + two cyan waves
 * below. Uses currentColor on the shield outline so it inherits from the
 * tone, while the waves and ship-deck stay cyan for brand consistency.
 */
function Mark({ size, tone }: { size: number; tone: Tone }) {
  const stroke = tone === 'dark' ? '#FFFFFF' : '#0F172A';
  const fill   = tone === 'dark' ? '#FFFFFF' : '#0F172A';
  const cyan   = '#06B6D4';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Shield body — rounded shoulders, pointed lower vertex */}
      <path
        d="M32 4 L54 12 V28 C54 41 45 51 32 58 C19 51 10 41 10 28 V12 Z"
        stroke={stroke}
        strokeWidth="3.5"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Ship hull — trapezoid with sloped bow */}
      <path
        d="M18 36 L46 36 L42 44 L22 44 Z"
        fill={fill}
      />
      {/* Bridge / wheelhouse */}
      <rect x="26" y="28" width="10" height="8" rx="1" fill={fill} />
      {/* Funnel */}
      <rect x="36" y="22" width="3.5" height="6" rx="0.5" fill={fill} />
      {/* Mast */}
      <rect x="28.5" y="20" width="1.5" height="8" fill={fill} />
      {/* Two cyan waves below the shield */}
      <path
        d="M6 56 C12 52, 18 60, 26 56 S40 52, 48 56 S58 52, 62 56"
        stroke={cyan}
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M2 62 C8 58, 14 66, 22 62 S36 58, 44 62 S58 58, 62 62"
        stroke={cyan}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeOpacity="0.6"
        fill="none"
      />
    </svg>
  );
}

/** Convenience: just the mark, sized to a px value. */
export function BrandMark({
  size = 32,
  tone = 'dark',
  className = '',
}: { size?: number; tone?: Tone; className?: string }) {
  return (
    <span
      className={`inline-flex items-center justify-center ${className}`}
      style={{ height: size, width: size }}
      aria-hidden="true"
    >
      <Mark size={size} tone={tone} />
    </span>
  );
}
